import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  CheckCircle, 
  AlertTriangle,
  Loader2
} from "lucide-react";

export default function TryoutRegistration({ userId }) {
  const { programId } = useParams();
  const navigate = useNavigate();

  // --- STATE ---
  const [program, setProgram] = useState(null);
  const [tryouts, setTryouts] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false); // For register/unregister spinner
  const [error, setError] = useState(null);

  // Modal State
  const [unregisterTarget, setUnregisterTarget] = useState(null);

  // --- FETCH DATA ---
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // 1. Fetch Program
        const { data: programData, error: programError } = await supabase
          .from("programs")
          .select("*")
          .eq("id", programId)
          .single();

        if (programError) throw programError;
        setProgram(programData);

        // 2. Fetch Tryouts (We fetch all, then filter in JS to handle Timezones safely)
        const { data: tryoutData, error: tryoutError } = await supabase
          .from("tryouts")
          .select("*")
          .eq("program_id", programId)
          .order('tryout_date', { ascending: true }); // Soonest first

        if (tryoutError) throw tryoutError;
        setTryouts(tryoutData || []);

        // 3. Fetch User Registrations
        if (userId) {
          const { data: regData, error: regError } = await supabase
            .from("tryout_registrations")
            .select("*")
            .eq("athlete_id", userId);
          
          if (regError) throw regError;
          setRegistrations(regData || []);
        }

      } catch (err) {
        console.error("Error loading tryout data:", err);
        setError("Failed to load program details.");
      } finally {
        setLoading(false);
      }
    }

    if (programId) loadData();
  }, [programId, userId]);

  // --- HELPERS ---

  // Filter for upcoming dates only
  const upcomingTryouts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison

    return tryouts.filter((t) => {
      if (!t.tryout_date) return false;
      const tDate = new Date(t.tryout_date);
      // Adjust comparison as needed (e.g., allow tryouts happening today)
      return tDate >= today;
    });
  }, [tryouts]);

  const isRegistered = (tryoutId) => {
    return registrations.some((r) => r.tryout_id === tryoutId);
  };

  // Date formatter (e.g., "Mon, Oct 12")
  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // --- HANDLERS ---

  const handleRegister = async (tryoutId) => {
    if (!userId) {
      alert("You must be logged in to register."); // Or navigate to login
      return;
    }
    setActionLoading(true);
    try {
      const { error } = await supabase.from("tryout_registrations").insert([
        { athlete_id: userId, tryout_id: tryoutId },
      ]);

      if (error) throw error;

      // Optimistic update
      setRegistrations((prev) => [...prev, { athlete_id: userId, tryout_id: tryoutId }]);
    } catch (err) {
      console.error("Registration error:", err);
      alert("Failed to register for tryout.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!unregisterTarget) return;
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from("tryout_registrations")
        .delete()
        .eq("athlete_id", userId)
        .eq("tryout_id", unregisterTarget.id);

      if (error) throw error;

      setRegistrations((prev) => prev.filter((r) => r.tryout_id !== unregisterTarget.id));
      setUnregisterTarget(null); // Close modal
    } catch (err) {
      console.error("Unregister error:", err);
      alert("Failed to unregister.");
    } finally {
      setActionLoading(false);
    }
  };

  // --- UI RENDER ---

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <p className="text-red-500 mb-4">{error || "Program not found"}</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 underline">Go Back</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 font-sans text-slate-800">
      
      {/* HEADER */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center text-slate-500 hover:text-blue-600 transition mb-4 font-medium"
        >
          <ArrowLeft size={18} className="mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Program
        </button>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b pb-4 border-slate-200">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{program.name}</h1>
            <p className="text-slate-500 mt-1">Select a tryout session below to secure your spot.</p>
          </div>
          <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
            {upcomingTryouts.length} Upcoming Sessions
          </div>
        </div>
      </div>

      {/* TRYOUT LIST */}
      {upcomingTryouts.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <Calendar size={48} className="mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-semibold text-slate-700">No Upcoming Tryouts</h3>
          <p className="text-slate-500 text-sm">Check back later for new dates.</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {upcomingTryouts.map((tryout) => {
            const registered = isRegistered(tryout.id);
            const dateObj = new Date(tryout.tryout_date);
            
            return (
              <li
                key={tryout.id}
                className={`group flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 rounded-xl border transition-all hover:shadow-md 
                  ${registered ? "bg-green-50 border-green-200" : "bg-white border-slate-200 hover:border-blue-300"}`}
              >
                {/* Left: Date & Info */}
                <div className="flex items-start gap-4 mb-4 sm:mb-0">
                  {/* Date Box */}
                  <div className={`flex flex-col items-center justify-center w-16 h-16 rounded-lg shrink-0 
                    ${registered ? "bg-white text-green-700 border border-green-200" : "bg-blue-50 text-blue-700"}`}>
                    <span className="text-xs font-bold uppercase">{dateObj.toLocaleString('default', { month: 'short' })}</span>
                    <span className="text-xl font-bold">{dateObj.getDate()}</span>
                  </div>

                  {/* Details */}
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                       {formatDate(tryout.tryout_date)}
                       {registered && <CheckCircle size={16} className="text-green-600" />}
                    </h3>
                    
                    <div className="flex items-center text-slate-600 text-sm mt-1 gap-4">
                       <span className="flex items-center gap-1">
                          <MapPin size={14} /> 
                          {tryout.location || "Location TBD"}
                       </span>
                    </div>
                    {/* Optional: Add Time here if you have a time column */}
                  </div>
                </div>

                {/* Right: Action Button */}
                <div>
                  {!registered ? (
                    <button
                      onClick={() => handleRegister(tryout.id)}
                      disabled={actionLoading}
                      className="px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 hover:shadow-md transition w-full sm:w-auto disabled:opacity-50"
                    >
                      {actionLoading ? "Processing..." : "Register Now"}
                    </button>
                  ) : (
                    <button
                      onClick={() => setUnregisterTarget(tryout)}
                      disabled={actionLoading}
                      className="px-6 py-2.5 rounded-lg bg-white border border-green-600 text-green-700 text-sm font-semibold shadow-sm hover:bg-red-50 hover:text-red-600 hover:border-red-600 transition w-full sm:w-auto"
                    >
                      <span className="group-hover:hidden">Registered</span>
                      <span className="hidden group-hover:inline">Unregister</span>
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* CONFIRMATION MODAL */}
      {unregisterTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden p-6 text-center">
            
            <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={24} />
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Registration?</h3>
            <p className="text-sm text-gray-600 mb-6">
              You are about to remove your spot for the tryout on <span className="font-semibold">{formatDate(unregisterTarget.tryout_date)}</span>.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setUnregisterTarget(null)}
                className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition"
              >
                Keep Spot
              </button>
              <button
                onClick={handleUnregister}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition shadow-sm"
              >
                Unregister
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}